/**
 * Pure helpers for `FleetScheduleMatrixTab.tsx` (Task 4 of the
 * route-schedule-ux-enhancements plan): the trucks x org-schedule row-join
 * and the "N trucks have no routes this week" summary count. Kept
 * dependency-free (no React/MUI/store imports, minimal shapes duplicated
 * rather than imported) so it can be unit tested directly in this repo's
 * Node-only vitest environment without needing a DOM -- mirrors the pattern
 * established by `truckRouteScheduleTab.helpers.ts`.
 */

/**
 * Joins the org's full truck list (`useTruckStore().trucks`) with the org
 * week-schedule response (`useOrganizationScheduleStore().schedules`), keyed
 * by `truck_id`. Every truck in `trucks` produces exactly one row -- a truck
 * with no matching schedule entry (or one with `days: []`) both resolve to
 * an empty `days` array here, never dropped. A schedule entry with no
 * matching truck (e.g. a truck deleted between the two fetches) is simply
 * not joined to anything and produces no row.
 *
 * Neither the truck list nor the schedule list is guaranteed to arrive in a
 * stable order from the backend (the org schedule endpoint has no
 * `ORDER BY` -- see `OrganizationWeekSchedule` in `types/routeSchedule.type.ts`),
 * so the result is always sorted by truck name for a predictable,
 * stable row order in the matrix. `days` is passed through as-is -- since a
 * weekday can now hold 2+ ordered routes (Task 3), a truck's joined `days`
 * may contain several entries sharing the same `day_of_week`; grouping those
 * back into a per-day list is `FleetScheduleMatrixTab.tsx`'s own concern at
 * render time (see `findScheduleDayEntries`), not this join's.
 */
export function joinTrucksWithSchedules<TTruck extends { id: string; name: string }, TDay>(
  trucks: readonly TTruck[],
  schedules: readonly { truck_id: string; days: readonly TDay[] }[],
): { truck: TTruck; days: readonly TDay[] }[] {
  const daysByTruckId = new Map(schedules.map((schedule) => [schedule.truck_id, schedule.days]));
  return trucks
    .map((truck) => ({ truck, days: daysByTruckId.get(truck.id) ?? [] }))
    .sort((a, b) => a.truck.name.localeCompare(b.truck.name));
}

/**
 * Count of joined rows whose truck has zero scheduled days this week --
 * backs the "N of M trucks have no routes scheduled this week" summary
 * stat. A truck absent from the schedule response and a truck present with
 * `days: []` are indistinguishable here (both already normalized to
 * `days: []` by `joinTrucksWithSchedules`), which is the desired behavior:
 * either way, the truck has nothing scheduled this week.
 */
export function countTrucksWithNoRoutesThisWeek(rows: readonly { days: readonly unknown[] }[]): number {
  return rows.filter((row) => row.days.length === 0).length;
}
